import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../state/authStore'
import { useUIStore } from '../state/uiStore'
import type { HeaderMenuItem } from '../components/common/Header'

/**
 * Hook useAppMenu — menu applicatif partagé entre tous les écrans.
 *
 * Ordre :
 *   [Utilisateur]
 *   [Changer le mot de passe]  — comptes email uniquement
 *   [Déconnexion]
 *   [Mode sombre / Mode clair]
 *   ── séparateur ──
 *   [À propos]
 *   [Aide]
 *
 * Usage :
 *   const { menuItems } = useAppMenu()
 *   <Header menuItems={menuItems} hideTheme />
 */
export function useAppMenu(): { menuItems: HeaderMenuItem[] } {
  const navigate = useNavigate()
  const { logout, user } = useAuthStore()
  const { toggleTheme, theme } = useUIStore()

  const handleLogout = () => {
    if (confirm('Voulez-vous vraiment vous déconnecter ?')) {
      logout()
      navigate('/login')
    }
  }

  const moonPath =
    'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z'
  const sunPath =
    'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z'

  const icon = (d: string) => (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
  )

  const menuItems: HeaderMenuItem[] = [
    {
      id: 'user',
      label: user?.name || user?.email || 'Utilisateur',
      icon: icon('M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'),
      onClick: () => {},
    },

    ...(!user?.oauth_provider
      ? ([
          {
            id: 'change-password',
            label: 'Changer le mot de passe',
            icon: icon(
              'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z'
            ),
            onClick: () => navigate('/change-password'),
          },
        ] as HeaderMenuItem[])
      : []),

    {
      id: 'logout',
      label: 'Déconnexion',
      icon: icon(
        'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
      ),
      onClick: handleLogout,
    },

    {
      id: 'theme',
      label: theme === 'light' ? 'Mode sombre' : 'Mode clair',
      icon: icon(theme === 'light' ? moonPath : sunPath),
      onClick: toggleTheme,
    },

    { id: 'sep', label: '', icon: null, onClick: () => {}, separator: true },

    {
      id: 'site',
      label: 'Site web',
      icon: icon(
        'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9'
      ),
      onClick: () => window.open('/site/', '_blank'),
    },

    {
      id: 'about',
      label: 'À propos',
      icon: icon('M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
      onClick: () => navigate('/about'),
    },

    {
      id: 'help',
      label: 'Aide',
      icon: icon(
        'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
      ),
      onClick: () => navigate('/help'),
    },
  ]

  return { menuItems }
}
